import React from 'react';
import { useApp } from '../../context/AppContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import SectionTitle from '../ui/SectionTitle';
import { linkifyProductos } from '../../lib/linkify';
import { perfilPersonal, idiomas, premios, docencia, libros, otros } from '../../data/perfil';
import './About.css';

interface Props {
  profileStatement: { es: string; en: string };
}

const About: React.FC<Props> = ({ profileStatement }) => {
  const { t, lang } = useApp();
  const ref = useScrollReveal();

  return (
    <section className="about" id="sobre-mi">
      <div className="about__inner" ref={ref as React.RefObject<HTMLDivElement>}>
        <SectionTitle
          eyebrow={lang === 'es' ? 'Sobre mí' : 'About'}
          title={lang === 'es' ? 'Trayectoria y perfil' : 'Track record & profile'}
          number="01"
        />

        <div className="about__grid">
          <div className="about__text">
            <p className="about__profile">{linkifyProductos(t(profileStatement))}</p>
            {t(perfilPersonal).split('\n').map((para, i) => (
              <p key={i} className="about__personal">{para}</p>
            ))}

            {/* Languages */}
            <div className="about__langs">
              {idiomas.map(l => (
                <div key={l.name.es} className="about__lang">
                  <span className="about__lang-name">{t(l.name)}</span>
                  <span className="about__lang-level">{t(l.level)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="about__awards">
            <h3 className="about__awards-title">
              {lang === 'es' ? 'Docencia' : 'Teaching'}
            </h3>
            <ul className="about__awards-list">
              {docencia.map(d => (
                <li key={d.title.es}>
                  <span className="about__award-year">{d.period}</span>
                  {t(d.title)} · {d.institution}
                </li>
              ))}
            </ul>

            <h3 className="about__awards-title" style={{ marginTop: '2rem' }}>
              {lang === 'es' ? 'Reconocimientos' : 'Awards'}
            </h3>
            <ul className="about__awards-list">
              {premios.map(p => (
                <li key={p.title.es}>
                  <span className="about__award-year">{p.year}</span>
                  {t(p.title)}
                </li>
              ))}
            </ul>

            <h3 className="about__awards-title" style={{ marginTop: '2rem' }}>
              {lang === 'es' ? 'Publicaciones' : 'Publications'}
            </h3>
            <div className="about__books">
              {libros.map(b => {
                const inner = (
                  <>
                    {b.cover ? (
                      <img
                        src={`${process.env.PUBLIC_URL}/${b.cover}`}
                        alt={b.title.es}
                        className="about__book-cover"
                      />
                    ) : (
                      <div className="about__book-icon">{b.icon}</div>
                    )}
                    <div>
                      <p className="about__book-title">{t(b.title)}</p>
                      {b.subtitle && (
                        <p className="about__book-subtitle">{t(b.subtitle)}</p>
                      )}
                      <p className="about__book-meta">{t(b.meta)}</p>
                    </div>
                  </>
                );
                return b.link ? (
                  <a
                    className="about__book about__book--link"
                    key={b.title.es}
                    href={b.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {inner}
                  </a>
                ) : (
                  <div className="about__book" key={b.title.es}>
                    {inner}
                  </div>
                );
              })}
            </div>

            <h3 className="about__awards-title" style={{ marginTop: '2rem' }}>
              {lang === 'es' ? 'Otros' : 'Other'}
            </h3>
            <ul className="about__awards-list">
              {otros.map(o => (
                <li key={o.label}>
                  <span className="about__award-year">{o.label}</span>
                  {t(o.text)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
